/**
 * Production: same-origin `/api` or `VITE_API_BASE_URL`.
 *
 * Dev (browser): `http://127.0.0.1:PORT/api` or `http://<LAN-ip>:PORT/api` — calls the Node API directly.
 * Some setups return 404 on POST via the Vite `/api` proxy; direct URL avoids that. CORS already allows
 * localhost / 127.0.0.1 / LAN in web/backend app.js.
 */
export function resolveApiBaseURL() {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw === 'string' && raw.trim()) {
    let trimmed = raw.replace(/\/$/, '');
    if (import.meta.env.DEV && trimmed.startsWith('/') && !trimmed.startsWith('/api')) {
      console.warn('[BWOF] VITE_API_BASE_URL must start with /api in dev; got:', trimmed, '— using /api');
      return '/api';
    }
    return trimmed;
  }

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    try {
      const proxy = import.meta.env.VITE_API_PROXY_TARGET || 'http://127.0.0.1:3000';
      const u = new URL(proxy.includes('://') ? proxy : `http://${proxy}`);
      const port = u.port || '3000';
      const h = window.location.hostname;
      const apiHost =
        h === 'localhost' || h === '127.0.0.1'
          ? '127.0.0.1'
          : /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h)
            ? h
            : '127.0.0.1';
      return `http://${apiHost}:${port}/api`;
    } catch {
      /* fall through */
    }
  }

  return '/api';
}

/** Ping URL (fetch + credentials). */
export function getSiteInspectionPingUrl() {
  const path = '/site-inspection-points/ping';
  const base = resolveApiBaseURL().replace(/\/$/, '');
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return `${base}${path}`;
  }
  return `${base || '/api'}${path}`;
}

/** Dev/prod-safe GET /api/ping URL (Login banner). */
export function getApiPingUrl() {
  const base = resolveApiBaseURL().replace(/\/$/, '');
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return `${base}/ping`;
  }
  return '/api/ping';
}

/**
 * Check BWOF API is reachable (not electricalapp on :3000).
 * @returns {Promise<{ ok: boolean, wrongApi?: string, message?: string }>}
 */
export async function probeBwofBackend() {
  const pingUrl = getApiPingUrl();
  try {
    const res = await fetch(pingUrl, { credentials: 'include' });
    let data = null;
    try {
      data = await res.json();
    } catch {
      /* non-JSON */
    }
    if (res.ok && data?.service === 'bwof-admin-api') {
      return { ok: true };
    }
    if (data?.service === 'electricalapp-api') {
      return {
        ok: false,
        wrongApi: 'electricalapp',
        message: 'Server API still points to electricalapp (:3000). Run RUN_DEPLOY.bat to fix nginx → :3010.',
      };
    }
    if (res.ok && data?.ok === true && !data?.service) {
      return { ok: true };
    }
    if (!res.ok) {
      return { ok: false, message: `API ping HTTP ${res.status}` };
    }
  } catch (err) {
    return { ok: false, message: err?.message || 'Connection failed' };
  }

  try {
    const res = await fetch('/api/health', { credentials: 'include' });
    const data = await res.json();
    if (data?.service === 'electricalapp-api') {
      return {
        ok: false,
        wrongApi: 'electricalapp',
        message: 'Server API still points to electricalapp (:3000). Run RUN_DEPLOY.bat to fix nginx → :3010.',
      };
    }
  } catch {
    /* ignore */
  }

  return { ok: false, message: 'BWOF API not responding. Deploy may be required.' };
}

/** Turn /api/files/... into a loadable image URL in dev (direct API host) and prod. */
export function resolveFileUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const apiBase = resolveApiBaseURL().replace(/\/$/, '');
  const origin = apiBase.replace(/\/api$/, '');
  if (url.startsWith('/api/')) return `${origin}${url}`;
  if (url.startsWith('/')) return `${origin}${url}`;
  return url;
}
